import type {
  AcquireBookPictures,
  AcquireBookZenbujiko,
  AllMattersAnalyzeOptions,
  PictureRequest,
  PictureTypeData,
  PictureTypeKey,
} from "@/types/acquirebook";
import type { OwnerRelatedRegistrySelection } from "@/types/ownerRelatedRegistries";
import { generateUniqueId } from "@/utils/generateUniqueId";
import { getPrefectureIdByName } from "@/utils/prefecture";
import {
  applyZenbujikoGenzaiTransform,
  transformChibanKaokuNumber,
} from "@/utils/utilsAcquireBook";

export interface ReusePictures {
  picture: AcquireBookPictures;
  reuseDetailId: number | null;
  getNewPicture: boolean;
}

export interface PictureRequestPayload {
  withOwnerAnalyze: boolean;
  withAllMattersAnalyze: boolean;
  identificationName: string;
  includeTitleSection?: boolean;
  includeKoku?: boolean;
  includeOtsuku?: boolean;
  analyzeMethodType?: "llm_api" | "rule_based";
  items: PictureRequest[];
}

interface PictureRequestEstimateItem extends Omit<PictureRequest, "unique_id"> {
  unique_id?: string;
}

export interface PictureRequestEstimatePayload
  extends Omit<PictureRequestPayload, "items"> {
  items: PictureRequestEstimateItem[];
}

interface PictureRequestPayloadParams {
  chibanKaokuNumbers: string[];
  prefecture: string;
  city: string;
  locationWithoutCity: string;
  locationFull: string;
  pastDays: number | "";
  realEstateType: string;
  requestOptions: PictureTypeData;
  withOwnerAnalyze: boolean;
  withAllMattersAnalyze: boolean;
  identificationName: string;
  reusePictures?: ReusePictures[];
  requestClosureBook?: boolean;
  ownerRelatedSelections?: OwnerRelatedRegistrySelection[];
  allMattersAnalyzeOptions?: AllMattersAnalyzeOptions;
}

type BasePictureRequestItem = Omit<PictureRequest, "unique_id">;

const buildBasePictureRequestItem = (
  prefecture: string,
  city: string,
  locationWithoutCity: string,
  locationFull: string,
  chibanKaokuNumber: string,
  pastDays: number | "",
  realEstateType: string,
  requestOptions: PictureTypeData,
  reusePictures: ReusePictures[],
  requestClosureBook = false
): BasePictureRequestItem => {
  const pictures: AcquireBookPictures[] = [];
  let zenbujikoOptions: AcquireBookZenbujiko[] = [];

  for (const key in requestOptions) {
    const typedKey = key as PictureTypeKey;
    switch (typedKey) {
      case "SHOYUSHAJIKO":
        if (requestOptions.SHOYUSHAJIKO.checked) {
          pictures.push(typedKey);
        }
        break;
      case "ZENBUJIKO":
        if (requestOptions.ZENBUJIKO.checked) {
          pictures.push(typedKey);
        }
        if (requestOptions.ZENBUJIKO.option.SHINTAKUMOKUROKU.checked) {
          zenbujikoOptions.push("SHINTAKUMOKUROKU");
        }
        if (requestOptions.ZENBUJIKO.option.KYODOTANPOMOKUROKU.checked) {
          zenbujikoOptions.push("KYODOTANPOMOKUROKU");
        }
        if (requestClosureBook) {
          zenbujikoOptions.push("HEISATOUKIBO");
        }
        break;
      case "CHISEKISOKURYOZU":
        if (requestOptions.CHISEKISOKURYOZU.checked) {
          pictures.push(typedKey);
        }
        break;
      case "DENSHIKOZU":
        if (requestOptions.DENSHIKOZU.checked) {
          pictures.push(typedKey);
        }
        break;
      case "CHIEKIKENZUMEN":
        if (requestOptions.CHIEKIKENZUMEN.checked) {
          pictures.push(typedKey);
        }
        break;
      case "TATEMONOZUMEN":
        if (requestOptions.TATEMONOZUMEN.checked) {
          pictures.push(typedKey);
        }
        break;
    }
  }

  if (requestOptions.ZENBUJIKO.option.GENZAIJIKO.checked) {
    zenbujikoOptions = applyZenbujikoGenzaiTransform(zenbujikoOptions);
  }

  const mappedReusePictures = reusePictures.map((reusePicture) => ({
    picture: reusePicture.picture,
    reuseDetailId: reusePicture.reuseDetailId ?? undefined,
    getNewPicture: reusePicture.getNewPicture,
  }));

  const normalizedChibanKaokuNumber =
    transformChibanKaokuNumber(chibanKaokuNumber);

  if (city === "" && locationFull !== "") {
    return {
      real_estate_type: realEstateType,
      pref_code: prefecture,
      location_full: locationFull,
      chiban_kaoku_number: normalizedChibanKaokuNumber,
      past_days: pastDays,
      pictures,
      zenbujiko_options: zenbujikoOptions,
      reuse_pictures: mappedReusePictures,
    };
  }

  return {
    real_estate_type: realEstateType,
    pref_code: prefecture,
    city_code: city,
    location_without_city: locationWithoutCity,
    chiban_kaoku_number: normalizedChibanKaokuNumber,
    past_days: pastDays,
    pictures,
    zenbujiko_options: zenbujikoOptions,
    reuse_pictures: mappedReusePictures,
  };
};

const makePictureRequestItem = (
  params: Omit<
    PictureRequestPayloadParams,
    | "withOwnerAnalyze"
    | "withAllMattersAnalyze"
    | "identificationName"
    | "ownerRelatedSelections"
  >
): PictureRequest => {
  return {
    ...buildBasePictureRequestItem(
      params.prefecture,
      params.city,
      params.locationWithoutCity,
      params.locationFull,
      params.chibanKaokuNumbers[0],
      params.pastDays,
      params.realEstateType,
      params.requestOptions,
      params.reusePictures ?? [],
      params.requestClosureBook
    ),
    unique_id: generateUniqueId(),
  };
};

const makePictureRequestEstimateItem = (
  params: Omit<
    PictureRequestPayloadParams,
    | "withOwnerAnalyze"
    | "withAllMattersAnalyze"
    | "identificationName"
    | "ownerRelatedSelections"
  >
): PictureRequestEstimateItem => {
  return buildBasePictureRequestItem(
    params.prefecture,
    params.city,
    params.locationWithoutCity,
    params.locationFull,
    params.chibanKaokuNumbers[0],
    params.pastDays,
    params.realEstateType,
    params.requestOptions,
    params.reusePictures ?? [],
    params.requestClosureBook
  );
};

const buildOwnerRelatedRequestItemBase = (
  selection: OwnerRelatedRegistrySelection,
  defaults: {
    prefCode: string;
    cityCode: string;
    locationWithoutCity: string;
    locationFull: string;
    pastDays: number | "";
  }
): BasePictureRequestItem => {
  const { registry, acquireOption } = selection;
  const resolvedPrefCode = (() => {
    if (registry.prefCode) {
      return registry.prefCode;
    }
    if (registry.prefectureName) {
      const prefId = getPrefectureIdByName(registry.prefectureName);
      if (prefId !== undefined) {
        return String(prefId);
      }
    }
    return undefined;
  })();
  const reusePictures: ReusePictures[] = [];
  const reuseSourceId =
    registry.retainedRegistry?.pictureRequestDetailId ??
    registry.retainedRegistry?.id ??
    registry.reuseRegistry?.pictureRequestDetailId ??
    registry.reuseRegistry?.id ??
    null;

  if (acquireOption === "retained" || acquireOption === "reuse") {
    if (reuseSourceId !== null) {
      reusePictures.push({
        picture: registry.pictureType,
        reuseDetailId: reuseSourceId,
        getNewPicture: false,
      });
    }
  } else if (acquireOption === "new") {
    reusePictures.push({
      picture: registry.pictureType,
      reuseDetailId: reuseSourceId,
      getNewPicture: true,
    });
  }

  return {
    real_estate_type: registry.realEstateType,
    pref_code: resolvedPrefCode ?? defaults.prefCode,
    city_code: registry.cityCode ?? undefined,
    location_without_city: registry.locationWithoutCity ?? undefined,
    location_full: registry.locationFull ?? registry.addressText ?? undefined,
    chiban_kaoku_number: registry.chibanKaokuNumber,
    past_days: defaults.pastDays,
    pictures: [registry.pictureType],
    zenbujiko_options: [],
    reuse_pictures: reusePictures.map((item) => ({
      picture: item.picture,
      reuseDetailId: item.reuseDetailId ?? undefined,
      getNewPicture: item.getNewPicture,
    })),
  };
};

const makeOwnerRelatedRequestItem = (
  selection: OwnerRelatedRegistrySelection,
  defaults: {
    prefCode: string;
    cityCode: string;
    locationWithoutCity: string;
    locationFull: string;
    pastDays: number | "";
  }
): PictureRequest => {
  return {
    ...buildOwnerRelatedRequestItemBase(selection, defaults),
    unique_id: generateUniqueId(),
  };
};

const makeOwnerRelatedRequestEstimateItem = (
  selection: OwnerRelatedRegistrySelection,
  defaults: {
    prefCode: string;
    cityCode: string;
    locationWithoutCity: string;
    locationFull: string;
    pastDays: number | "";
  }
): PictureRequestEstimateItem => {
  return buildOwnerRelatedRequestItemBase(selection, defaults);
};

export const makePictureRequestPayload = (
  params: PictureRequestPayloadParams
): PictureRequestPayload => {
  const {
    chibanKaokuNumbers,
    prefecture,
    city,
    locationWithoutCity,
    locationFull,
    pastDays,
    realEstateType,
    requestOptions,
    withOwnerAnalyze,
    withAllMattersAnalyze,
    identificationName,
    reusePictures = [],
    requestClosureBook = false,
    ownerRelatedSelections = [],
    allMattersAnalyzeOptions,
  } = params;

  const items = chibanKaokuNumbers.map((chibanKaokuNumber) =>
    makePictureRequestItem({
      chibanKaokuNumbers: [chibanKaokuNumber],
      prefecture,
      city,
      locationWithoutCity,
      locationFull,
      pastDays,
      realEstateType,
      requestOptions,
      reusePictures,
      requestClosureBook,
    })
  );

  if (ownerRelatedSelections.length > 0) {
    const defaults = {
      prefCode: prefecture,
      cityCode: city,
      locationWithoutCity,
      locationFull,
      pastDays,
    };
    items.push(
      ...ownerRelatedSelections.map((selection) =>
        makeOwnerRelatedRequestItem(selection, defaults)
      )
    );
  }

  return {
    withOwnerAnalyze,
    withAllMattersAnalyze,
    identificationName,
    ...(withAllMattersAnalyze && allMattersAnalyzeOptions),
    items,
  };
};

export const makePictureRequestEstimatePayload = (
  params: PictureRequestPayloadParams
): PictureRequestEstimatePayload => {
  const {
    chibanKaokuNumbers,
    prefecture,
    city,
    locationWithoutCity,
    locationFull,
    pastDays,
    realEstateType,
    requestOptions,
    withOwnerAnalyze,
    withAllMattersAnalyze,
    identificationName,
    reusePictures = [],
    requestClosureBook = false,
    ownerRelatedSelections = [],
    allMattersAnalyzeOptions,
  } = params;

  const items = chibanKaokuNumbers.map((chibanKaokuNumber) =>
    makePictureRequestEstimateItem({
      chibanKaokuNumbers: [chibanKaokuNumber],
      prefecture,
      city,
      locationWithoutCity,
      locationFull,
      pastDays,
      realEstateType,
      requestOptions,
      reusePictures,
      requestClosureBook,
    })
  );

  if (ownerRelatedSelections.length > 0) {
    const defaults = {
      prefCode: prefecture,
      cityCode: city,
      locationWithoutCity,
      locationFull,
      pastDays,
    };
    items.push(
      ...ownerRelatedSelections.map((selection) =>
        makeOwnerRelatedRequestEstimateItem(selection, defaults)
      )
    );
  }

  return {
    withOwnerAnalyze,
    withAllMattersAnalyze,
    identificationName,
    ...(withAllMattersAnalyze && allMattersAnalyzeOptions),
    items,
  };
};
